import {
  EntryInfoUnion,
  EntryListView,
  FiltersToApproveBody,
  FiltersWhoIsOutBody,
  RowSheetView,
  TimeEntryApprovalTime,
  TimeEntryForm,
  TimeEntrySheetView,
  FiltersBody,
} from "../types/times-module-types";
import { newUuid } from "../../SharedModule/utils/uuid";
import {
  ALL_COST_CENTERS,
  ALL_PROJECTS_AND_WORK_ORDERS,
  BAMBOO_HR,
  BILLABLE,
  UNPAID,
  PROJ_MEAL_BREAK_ID,
  PROJ_NURSING_MOTHER_ID,
  PROJ_PAID_TIME_OFF_ID,
  PROJ_UNPAID_TIME_OFF_ID,
  ROLE_PROJECT_OWNER,
  ROLE_SUPERVISOR,
  ROLE_SUPER_ADMIN,
  ROLE_TIMESHEETS_ADMIN,
  ROLE_TIMESHEETS_VIEWER,
  TASKTYPE_HOLIDAY_ID,
  TASKTYPE_MEAL_BREAK_ID,
  TASKTYPE_NURSING_MOTHER_ID,
  TASKTYPE_UNPAID_TIME_OFF_ID,
  closed_status,
} from "../../SharedModule/utils/constants";
import {
  quantityFormat,
  decodeURIComponentSafe,
} from "../../SharedModule/utils/formatters";
import {
  getDateFromDateString,
  getDateFromISOFormat,
  getIsoFormatStringWithTimeZone,
  isBetweenTwoDates,
  isToday,
} from "../../SharedModule/utils/dateUtils";
import { SelectItemNumber } from "../../SharedModule/types/shared-module-types";

export const empty: SelectItemNumber = {
  value: 0,
  label: "—",
};

export const hoursTypeOptions: SelectItemNumber[] = [
  {
    value: 1,
    label: "Client Billable",
  },
  {
    value: 2,
    label: "Client Non-billable",
  },
  {
    value: 3,
    label: "Internal",
  },
];

export const emptyProjectWorkOrder: SelectItemNumber = {
  value: 0,
  label: "Select a project, task or work order you are assigned to",
};

// Param is array of roles only for time module
export const hasTimeAdminAccess = (userRoles: string[] | undefined) => {
  let response: boolean = false;
  userRoles?.forEach((role: string) => {
    if (
      role === ROLE_SUPER_ADMIN ||
      role === ROLE_TIMESHEETS_ADMIN ||
      role === ROLE_SUPERVISOR ||
      role === ROLE_PROJECT_OWNER
    ) {
      response = true;
    }
  });
  return response;
};

// Param is array of roles only for time module
export const hasTimeViewerAccess = (userRoles: string[] | undefined) => {
  let response: boolean = false;
  userRoles?.forEach((role: string) => {
    if (role === ROLE_TIMESHEETS_VIEWER) {
      response = true;
    }
  });
  return response;
};

export const getEmptyTimeEntry = (day: Date) => {
  return {
    entryCanEdit: true,
    entryCanDelete: true,
    entryId: newUuid(),
    entryDate: getIsoFormatStringWithTimeZone(day),
    entryDateValid: true,
    entryProjectWorkOrder: emptyProjectWorkOrder,
    entryProjectWorkOrderValid: false,
    entryHours: "",
    entryHoursValid: false,
    entryTaskType: empty,
    entryTaskTypeValid: false,
    entryDescription: "",
    entryIsBillable: true,
    entryNonBillableReason: empty,
    entryNonBillableReasonValid: false,
    isEntryValid: false,
    isEntryTouched: false,
  };
};

export const isDateValidToEnterTime = (
  isSuperTimeAdmin,
  isSupervisorOrPO,
  isTimeViewer,
  activeUserId,
  actualUserValue,
  entryDate,
  previousWorkableDay,
  lastWorkableDay,
  usersAssignees
) => {
  let response = false;
  // super time admin => superAdmin and TimeAdmin
  if (
    isSuperTimeAdmin ||
    // supervisor and project owner for others
    (isSupervisorOrPO && activeUserId !== actualUserValue && !isTimeViewer) ||
    // supervisor and project owner and time viewer for others who belongs to "delegates"
    (isSupervisorOrPO &&
      activeUserId !== actualUserValue &&
      isTimeViewer &&
      !!usersAssignees?.find((user) => user.value === actualUserValue)) ||
    // supervisor and project owner for himself, time viewer for himself and common users
    (getDateFromISOFormat(
      getIsoFormatStringWithTimeZone(previousWorkableDay)
    ) <= getDateFromISOFormat(getIsoFormatStringWithTimeZone(entryDate)) &&
      entryDate < lastWorkableDay &&
      ((isSupervisorOrPO && activeUserId === actualUserValue) ||
        (!isSupervisorOrPO &&
          isTimeViewer &&
          activeUserId === actualUserValue) ||
        (!isSupervisorOrPO && !isTimeViewer)))
  ) {
    response = true;
  }

  return response;
};

export const getCategoryId = (list: any, value: number) => {
  let selected = list.find((elem) => elem.entityId === value);
  return selected.categoryId;
};

export const getProjectWorkOrder = (list: any, value: number) => {
  let selected = list.find((elem) => elem.entityId === value);
  return selected;
};

export const getValueFromSelect = (list: any, value: number | null) => {
  let selected = list?.find((elem) => elem.value === value);
  return selected;
};

export const getValueFromSelectWithLabel = (list: any, label: string) => {
  let selected = list.find((elem) => elem.label === label);
  return selected;
};

export const camelCaseToWords = (s: string) => {
  const result = s.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const firstLetterToLower = (word: string) => {
  return word.charAt(0).toLowerCase() + word.slice(1);
};

// ASUITE11-4412, S3776: transformValue's single switch + nested if/else-if
// chains put every branch at nesting depth 2-4, which is what drove its
// complexity past 15 despite each branch being simple on its own. Splitting
// by case-group and using early returns (no else/else-if) keeps every branch
// at depth 0-1. joinLabels replaces the three near-identical
// forEach+accumulate-with-separator blocks — same "first item has no
// separator" behavior, just not repeated three times.
const ARRAY_LABEL_PROPERTIES = new Set([
  "usersAssignees",
  "costCenter",
  "client",
  "project",
  "projectManager",
  "user",
  "taskType",
  "country",
  "nonBillableReasons",
  "projectOwner",
  "billableType",
  "costCentersIDs",
]);

const FLAG_LABEL_PROPERTIES = new Set(["hourType", "timeOff", "approvalStatus"]);

const joinLabels = (
  items: any[],
  formatItem: (item: any) => string,
  separator: string
): string | undefined => {
  let response: string | undefined;
  items.forEach((item) => {
    response = response ? response + separator + formatItem(item) : formatItem(item);
  });
  return response;
};

const transformArrayValue = (property: string, value: any) => {
  if (value === null || value.length === 0) {
    if (property === "project") return ALL_PROJECTS_AND_WORK_ORDERS;
    if (property === "costCentersIDs") return ALL_COST_CENTERS;
    return "All " + camelCaseToWords(property);
  }
  if (property === "user") {
    return joinLabels(value, (element) => element.label, " - ");
  }
  if (property === "usersAssignees") {
    return joinLabels(
      value,
      (element) => element.name + (element.active ? "" : " (X)"),
      " - "
    );
  }
  return joinLabels(value, (element) => element.label, ", ");
};

const transformFlagValue = (property: string, value: any) => {
  if (
    property === "timeOff" &&
    !value["paidTimeOff"] &&
    !value["unpaidTimeOff"]
  ) {
    return "None";
  }

  let response: string | undefined;
  for (const prop in value) {
    if (!value[prop]) continue;
    response = response
      ? response + ", " + camelCaseToWords(prop)
      : camelCaseToWords(prop);
  }
  return response;
};

export const transformValue = (property: string, value: any) => {
  if (ARRAY_LABEL_PROPERTIES.has(property)) {
    return transformArrayValue(property, value);
  }
  if (FLAG_LABEL_PROPERTIES.has(property)) {
    return transformFlagValue(property, value);
  }
  return undefined;
};

// ASUITE11-4412, S3776: preparedFilters' complexity came from sheer count —
// ~30 independent sibling ifs/ternaries, each only +1 but with no nesting to
// collapse. Extracting each filter's own computation into its own function
// moves that count into separate complexity budgets; selectedIds/mapIdsOrNull
// replace two repeated shapes (4+ manual "if (flag) push(id)" ifs, and
// "=== null ? null : sendNullIfEmpty(list.map(el => el.value))") with a
// single reusable, control-flow-free expression each.
const HOUR_TYPE_IDS: Record<string, number> = {
  billableHours: 1,
  nonBillableHours: 2,
  internalHours: 3,
  financedHours: 4,
};
const TIME_OFF_IDS: Record<string, number> = {
  paidTimeOff: 1,
  unpaidTimeOff: 2,
};
const APPROVAL_STATUS_IDS: Record<string, number> = {
  pendingHours: 1,
  approvedHours: 2,
  lockedHours: 3,
};
const SUMMARY_IDS: Record<string, number> = {
  byUser: 1,
  byTask: 2,
  byTaskType: 3,
  hideEntries: 4,
};

const selectedIds = (
  flags: Record<string, boolean>,
  idsByKey: Record<string, number>
): number[] =>
  Object.keys(idsByKey)
    .filter((key) => flags[key])
    .map((key) => idsByKey[key]);

const mapIdsOrNull = (list: any[] | null) =>
  list === null ? null : sendNullIfEmpty(list.map((elem) => elem.value));

const computeProjectManagerIds = (filters: any) => {
  let projectManagerInfo = [];
  if (filters.projectManager) {
    filters.projectManager.forEach((proj) => {
      projectManagerInfo = projectManagerInfo.concat(proj.projectIds);
    });
  }
  return filters.projectManager === null ? null : projectManagerInfo;
};

const computeProjectsInfo = (filters: any) => {
  let projectsInfo: any[] = [];
  // send null id all project and all work orders are selected
  if (
    !(
      filters.project?.length === 2 &&
      filters.project?.every((proj) => proj.value < 0)
    )
  ) {
    if (filters.project) {
      filters.project.forEach((proj) => {
        projectsInfo.push({
          categoryId: proj.categoryId,
          entityId: proj.entityId,
        });
      });
    }
  }
  return projectsInfo;
};

// ASUITE11-2876: user is DTA and cc filter is null or empty, must send all cc ids (from 6100 to 6299)
const computeCostCenterResponse = (
  filters: any,
  isOnlyDeliveryTimeAdmin: boolean,
  costCentersQuery: any
) =>
  filters.costCenter === null ||
  filters.costCenter.map((elem) => elem.value).length === 0
    ? isOnlyDeliveryTimeAdmin
      ? costCentersQuery
          .filter((cc) => cc.value >= 6100 && cc.value <= 6299)
          .map((elem) => elem.value)
      : null
    : sendNullIfEmpty(filters.costCenter.map((elem) => elem.value));

export const preparedFilters = (
  filters: any,
  isOnlyDeliveryTimeAdmin: boolean,
  costCentersQuery: any
) => {
  const projectsInfo = computeProjectsInfo(filters);
  const hourTypeInfo = selectedIds(filters.hourType, HOUR_TYPE_IDS);
  const timeOffInfo = selectedIds(filters.timeOff, TIME_OFF_IDS);
  const approvalStatusInfo = selectedIds(
    filters.approvalStatus,
    APPROVAL_STATUS_IDS
  );
  const summariesIdsInfo = selectedIds(filters.summary, SUMMARY_IDS);

  const response: FiltersBody = {
    startDate: filters.startDate,
    endDate: filters.endDate,
    costCentersIds: computeCostCenterResponse(
      filters,
      isOnlyDeliveryTimeAdmin,
      costCentersQuery
    ),
    clientIds: mapIdsOrNull(filters.client),
    entities: sendNullIfEmpty(projectsInfo),
    managersProjects: computeProjectManagerIds(filters),
    userIds: mapIdsOrNull(filters.user),
    taskTypeIds: mapIdsOrNull(filters.taskType),
    countries: mapIdsOrNull(filters.country),
    hourTypes: sendNullIfEmpty(hourTypeInfo),
    nonBillableReasonsIds: mapIdsOrNull(filters.nonBillableReasons),
    timeOff: sendNullIfEmpty(timeOffInfo),
    statusIds: sendNullIfEmpty(approvalStatusInfo),
    summariesIds: sendNullIfEmpty(summariesIdsInfo),
  };
  return response;
};

export const navigateTo = (entry: EntryListView | TimeEntryApprovalTime) => {
  let url: string = "";
  switch (entry.categoryId) {
    case 1:
      //project
      url = `${process.env.REACT_APP_ASUITE_PROJECT_URI}${entry.entityId}`;
      break;
    case 2:
      // task
      url = `${process.env.REACT_APP_ASUITE_PROJECT_URI}${entry.projectId}`;
      break;
    default:
      // work order
      url = `${process.env.REACT_APP_ASUITE_WO_URI}${entry.entityId}`;
      break;
  }
  return url;
};

export const findEntityId = (structure: any) => {
  let responseId: number = 0;
  Object.keys(structure).forEach((key: string) => {
    if (structure[key].timeEntries.length > 0 && responseId === 0) {
      responseId = structure[key].timeEntries[0].entityId;
    }
  });
  return responseId;
};

export const navigateToFromSheetView = (entry: RowSheetView) => {
  let url: string = "";
  switch (entry.categoryId) {
    case 1:
      //project
      url = `${process.env.REACT_APP_ASUITE_PROJECT_URI}${entry.entityId}`;
      break;
    case 2:
      // task
      url = `${process.env.REACT_APP_ASUITE_PROJECT_URI}${entry.projectId}`;
      break;
    default:
      // work order
      let partialEntiryId: number = findEntityId(entry.dailyDetails);
      url = `${process.env.REACT_APP_ASUITE_WO_URI}${
        partialEntiryId ? partialEntiryId : entry.entityId
      }`;
      break;
  }
  return url;
};

// creates an object, filters by any property
export const groupByProperty = (array, property) => {
  let hash = {};

  for (let elem of array) {
    if (!hash[elem[property]]) hash[elem[property]] = [];
    hash[elem[property]].push(elem);
  }
  return hash;
};

// to avoid issue of 2 structures separated by categoryId
export const concatProjectsWorkOrders = (projectsWorkOrders) => {
  return projectsWorkOrders[0].options.concat(projectsWorkOrders[1].options);
};

const replaceKeyByCorrectName = (str) => {
  switch (str) {
    case "EntryDate":
      return "Date";
    case "Project":
      return "Project / Work order";
    case "NonBillableReason":
      return "Non-billable reason";
    case "TimeEntryType":
      return "Billable";
    case "TaskType":
      return "Task type";
    default:
      return str;
  }
};
// ASUITE11-4412, S3776: the old/new-value ternary was duplicated verbatim
// (only which object it reads differs), each copy nested inside the for-in's
// if — extracting it to formatAuditValue and using `continue` instead of
// wrapping the push in an if removes both the duplication and the nesting.
const formatAuditValue = (key: string, value: any) =>
  value
    ? key === "Description"
      ? decodeURIComponentSafe(value)
      : value
    : "N/A";

export const formatAuditNewChanges = (oldValues: any, newValues: any) => {
  let response: string[] = [];

  for (const key in oldValues) {
    if (oldValues[key] === newValues[key]) continue;
    response.push(
      `${replaceKeyByCorrectName(key)}: ${formatAuditValue(
        key,
        oldValues[key]
      )} → ${formatAuditValue(key, newValues[key])}`
    );
  }
  return response;
};

// Sum of hours worked from diff subtask in call of project (sheet view)
export const sumHoursWorked = (dailyDetails: any[]) => {
  let sum = 0;
  dailyDetails.forEach((element) => {
    sum = sum + element.hoursWorked;
  });
  return quantityFormat(sum);
};

export const getCellClassTodayClickable = (condition: any) => {
  if (
    (typeof condition === "string" &&
      isToday(getDateFromDateString(condition))) ||
    (typeof condition === "object" && isToday(condition))
  ) {
    return "today-clickable";
  } else {
    return "time-entry-clickable";
  }
};

// To load select grouped by
export const getProjectOrWorkOrderGroupedBy = (structure: any) => {
  if (structure && structure.length > 0) {
    return [
      {
        label: "All",
        options: structure?.filter((item) => {
          if (item.categoryId < 0) {
            return item;
          }
          return null;
        }),
      },
      {
        label: "Projects",
        options: structure?.filter((item) => {
          if (item.categoryId === 1) {
            return item;
          }
          return null;
        }),
      },
    ];
  } else {
    return [];
  }
};

export const getTaskCategoryName = (value: number) => {
  switch (value) {
    case 1:
      return "Ordinary";
    case 2:
      return "Special";
    case 3:
      return "Time Off";
    default:
      return "";
  }
};

export const getTimeEntryTypeLabel = (timeEntryType: string): string => {
  if (timeEntryType === BILLABLE) {
    return "Yes";
  }
  if (timeEntryType === UNPAID) {
    return "Unpaid";
  }
  return "No";
};

export const setTimeEntryTypeIdforRequest = (entry: TimeEntryForm) => {
  return entry.entryTaskType.value === TASKTYPE_NURSING_MOTHER_ID
    ? 4
    : entry.entryTaskType.value === TASKTYPE_UNPAID_TIME_OFF_ID ||
      entry.entryTaskType.value === TASKTYPE_MEAL_BREAK_ID
    ? 5
    : entry.entryIsBillable
    ? 1
    : 2;
};

export const checkMealBreakOrNursingMother = (dailyDetails: any[]) => {
  let response = false;
  Object.keys(dailyDetails).forEach((elem) => {
    if (dailyDetails[elem].timeEntries.length > 0) {
      response =
        dailyDetails[elem].timeEntries[0].entityId === PROJ_MEAL_BREAK_ID ||
        dailyDetails[elem].timeEntries[0].entityId === PROJ_NURSING_MOTHER_ID
          ? true
          : false;
    }
  });
  return response;
};

export const preparedFiltersToApprove = (
  filters: any,
  isSuperTimeAdmin: boolean,
  isSupervisorOrPO: boolean,
  managersApprovalTime: any,
  costCentersApprovalTimeComplete: any,
  projectsApprovalTimeFiltered: any
) => {
  // projectManagers
  let projectManagerInfo: number[] = [];
  if (filters.projectOwner && filters.projectOwner.length > 0) {
    filters.projectOwner.forEach((proj) => {
      projectManagerInfo = projectManagerInfo.concat(proj.projectIds);
    });
  } else if (isSupervisorOrPO && !isSuperTimeAdmin) {
    managersApprovalTime.forEach((proj) => {
      projectManagerInfo = projectManagerInfo.concat(proj.projectIds);
    });
  }

  // cost Centers
  let costCenterInfo: any[] = [];
  if (filters.costCenter && filters.costCenter.length > 0) {
    filters.costCenter.forEach((costCenter) => {
      costCenterInfo = costCenterInfo.concat(
        getCostCenterChildrenIds(
          costCentersApprovalTimeComplete,
          costCenter.value,
          "id"
        )
      );
    });
  }

  // remove duplicates
  costCenterInfo = costCenterInfo.filter((item, pos) => {
    return costCenterInfo.indexOf(item) === pos;
  });

  // projects
  let projectsInfo: any[] = [];
  if (filters.project && filters.project.length > 0) {
    filters.project.forEach((proj) => {
      projectsInfo.push({
        categoryId: proj.categoryId,
        entityId: proj.value,
      });
    });
  } else {
    projectsApprovalTimeFiltered.forEach((proj) => {
      projectsInfo.push({
        categoryId: proj.categoryId,
        entityId: proj.value,
      });
    });
  }

  let response: FiltersToApproveBody = {
    endDate: filters.endDate,
    costCentersIds: sendNullIfEmpty(costCenterInfo),
    entities: sendNullIfEmpty(projectsInfo),
    managersProjects: sendNullIfEmpty(projectManagerInfo),
    hourTypes:
      filters.billableType === null || filters.billableType.length === 0
        ? null
        : filters.billableType.map((elem) => elem.value),
  };
  return response;
};

export const isEntryTimeOff = (
  day: RowSheetView | EntryListView | TimeEntrySheetView
) => {
  return (
    day.entityId === PROJ_PAID_TIME_OFF_ID ||
    day.entityId === PROJ_UNPAID_TIME_OFF_ID
  );
};

// ASUITE11-4363: classify a raw entity id as a time-off project (Paid/Unpaid
// Time Off). Used to tell "excluded from self-view by design" apart from
// "project is genuinely inactive" when an existing entry can't be resolved.
export const isTimeOffProjectId = (id: number | undefined | null) => {
  return id === PROJ_PAID_TIME_OFF_ID || id === PROJ_UNPAID_TIME_OFF_ID;
};

export const isRegularTime = (entry: EntryInfoUnion) => {
  return (
    entry.entityId !== PROJ_PAID_TIME_OFF_ID &&
    entry.entityId !== PROJ_UNPAID_TIME_OFF_ID &&
    entry.entityId !== PROJ_NURSING_MOTHER_ID &&
    entry.entityId !== PROJ_MEAL_BREAK_ID
  );
};

export const isPaidTimeOff = (entry: EntryInfoUnion) => {
  return entry.entityId === PROJ_PAID_TIME_OFF_ID;
};

export const isUnpaidTimeOff = (entry: EntryInfoUnion) => {
  return (
    entry.entityId === PROJ_NURSING_MOTHER_ID ||
    entry.entityId === PROJ_MEAL_BREAK_ID ||
    entry.entityId === PROJ_UNPAID_TIME_OFF_ID
  );
};

export const isNursingOrMealBreak = (entry: EntryInfoUnion) => {
  return (
    entry.entityId === PROJ_NURSING_MOTHER_ID ||
    entry.entityId === PROJ_MEAL_BREAK_ID ||
    (entry.entityId === PROJ_UNPAID_TIME_OFF_ID &&
      entry.taskTypeId === TASKTYPE_NURSING_MOTHER_ID) ||
    (entry.entityId === PROJ_UNPAID_TIME_OFF_ID &&
      entry.taskTypeId === TASKTYPE_MEAL_BREAK_ID)
  );
};

export const getCorrectHoursType = (hoursType: string) => {
  switch (hoursType) {
    case "billableHours":
      return "Billable Hours Only";
    case "nonBillableHours":
      return "Non Billable Hours Only";
    case "internalHours":
      return "Internal Hours Only";
    case "workOrderHours":
      return "Work Order Hours Only";
    case "pendingHours":
      return "All Pending Hours";
    default:
      break;
  }
};

export const isObjectEmpty = (objectName) => {
  return (
    Object.keys(objectName).length === 0 && objectName.constructor === Object
  );
};

export const sendNullIfEmpty = (actualArray: any[]) => {
  return actualArray.length > 0 ? actualArray : null;
};

export const isNotNullOrEmpty = (actualArray: any) => {
  return actualArray !== null && actualArray?.length > 0;
};

// ASUITE11-2477: Changing a holiday (task type) - only from bamboo super admin and for others
export const canEditHolidayFromBamboo = (
  entry: EntryListView | TimeEntryApprovalTime,
  activeUserId: any,
  userId: any
): boolean => {
  return (
    entry.timeEntrySourceId === 2 &&
    entry.source === BAMBOO_HR &&
    entry.taskTypeId === TASKTYPE_HOLIDAY_ID &&
    activeUserId !== userId
  );
};

// ASUITE11-2512
export const isClosed = (status: string) => {
  return closed_status.includes(status);
};

// ASUITE11-2573
export const isProjectClosed = (
  entry: EntryListView | TimeEntryApprovalTime | TimeEntrySheetView
) => {
  let response: boolean = false;
  // for WO (categoryId === 3) => verify if projectID !== null before check projectStatus
  if (entry.categoryId === 3 && entry.projectId && entry.projectStatus) {
    response = isClosed(entry.projectStatus);
  }
  if (entry.categoryId !== 3 && entry.projectStatus) {
    response = isClosed(entry.projectStatus);
  }

  return response;
};

export const isImportedFromBambooNotHolidayChecker = (
  entry: EntryListView | TimeEntrySheetView
) => {
  return (
    entry.timeEntrySourceId === 2 &&
    entry.source === BAMBOO_HR &&
    entry.taskTypeId !== TASKTYPE_HOLIDAY_ID
  );
};

// Shared by iconEditEnabled/iconDeleteEnabled — the four "who is asking" role
// flags always travel together, so they're grouped into one object instead of
// four positional parameters. That keeps isIconEnabledForSelf/isIconEnabledForOthers
// under SonarQube's S107 parameter-count limit (7) without changing behavior.
type IconRoleContext = {
  isSuperTimeAdmin: boolean;
  isSupervisorOrPO: boolean;
  isDeliveryTimeAdmin: boolean;
  isTimeViewer: boolean;
};

// Shared by iconEditEnabled/iconDeleteEnabled (SonarQube S3776/duplication —
// ASUITE11-4389). Both functions were ~90% identical; this holds the "FOR
// HIMSELF" branch, which is byte-identical between the two except for which
// permission field gates the check — resolved by the caller into
// `hasPermission` (entry.canEdit for edit, entry.canDelete for delete) so
// this helper doesn't need to know either field name.
// ASUITE11-4412, S3776: each role branch below used to be two sibling
// `if (cond) response = true;` statements nested inside the role if/else-if/
// else — since `response` only ever moves false→true and nothing runs
// between the two ifs in a branch, that's equivalent to returning their OR
// directly. Flattening to early returns removes the nesting-depth bonus the
// nested ifs cost (same conditions, same order, same result).
const isIconEnabledForSelf = (
  entry: EntryInfoUnion,
  roles: IconRoleContext,
  previousWorkableDay: Date | null,
  today: Date | null,
  lastWorkableDay: Date | null,
  hasPermission: boolean
): boolean => {
  const { isSuperTimeAdmin, isSupervisorOrPO, isDeliveryTimeAdmin, isTimeViewer } =
    roles;

  // for super admis
  if (isSuperTimeAdmin) {
    return (
      // unpaid time off
      (isNursingOrMealBreak(entry) &&
        (entry.timeEntryStatusId === 1 || entry.timeEntryStatusId === 2)) ||
      // regular hours
      (isRegularTime(entry) &&
        (entry.timeEntryStatusId === 1 || entry.timeEntryStatusId === 2))
    );
  }

  // supervisor or project owners or delivery time admin
  if (isSupervisorOrPO || isDeliveryTimeAdmin) {
    return (
      // unpaid time off
      (isNursingOrMealBreak(entry) &&
        entry.timeEntryStatusId === 1 &&
        isBetweenTwoDates(entry.entryDate, previousWorkableDay, today) &&
        (isTimeViewer ? hasPermission : true)) ||
      // regular hours
      (isRegularTime(entry) &&
        hasPermission &&
        entry.timeEntryStatusId === 1 &&
        isBetweenTwoDates(entry.entryDate, null, lastWorkableDay))
    );
  }

  // for common users
  return (
    // unpaid time off
    (isNursingOrMealBreak(entry) &&
      entry.timeEntryStatusId === 1 &&
      isBetweenTwoDates(entry.entryDate, previousWorkableDay, today)) ||
    // regular hours
    (isRegularTime(entry) &&
      hasPermission &&
      entry.timeEntryStatusId === 1 &&
      isBetweenTwoDates(entry.entryDate, null, lastWorkableDay))
  );
};

// Shared by iconEditEnabled/iconDeleteEnabled — the part of "FOR OTHERS" that
// is identical between the two. What is NOT shared, on purpose: the outer
// "FOR OTHERS" guard and the superTimeAdmin Bamboo-import check differ by a
// real business rule (ASUITE11-2736 lets delete reach locked/Bamboo entries
// that edit cannot), so each caller still computes its own guard and passes
// the resolved Bamboo grant in as `superAdminBambooGrant`
// (canEditHolidayFromBamboo(...) for edit, isImportedFromBambooNotHoliday for
// delete) rather than that rule living here.
// ASUITE11-4412, S3776: same flattening as isIconEnabledForSelf above — each
// role branch's sibling `if (cond) response = true;` statements become one
// returned OR expression, since response only ever moves false→true.
const isIconEnabledForOthers = (
  entry: EntryInfoUnion,
  roles: IconRoleContext,
  previousWorkableDay: Date | null,
  today: Date | null,
  hasPermission: boolean,
  superAdminBambooGrant: boolean
): boolean => {
  const { isSuperTimeAdmin, isSupervisorOrPO, isDeliveryTimeAdmin, isTimeViewer } =
    roles;

  if (isSuperTimeAdmin) {
    return (
      superAdminBambooGrant ||
      isUnpaidTimeOff(entry) ||
      (isPaidTimeOff(entry) && entry.timeEntrySourceId === 1) ||
      // regular hours
      (isRegularTime(entry) &&
        (entry.timeEntryStatusId === 1 || entry.timeEntryStatusId === 2))
    );
  }

  // supervisor or project owners or delivery time admin
  if (isSupervisorOrPO || isDeliveryTimeAdmin) {
    return (
      // unpaid time off
      (isNursingOrMealBreak(entry) &&
        entry.timeEntryStatusId === 1 &&
        isBetweenTwoDates(entry.entryDate, previousWorkableDay, today) &&
        (isTimeViewer ? hasPermission : true)) ||
      // regular hours
      (isRegularTime(entry) && hasPermission && entry.timeEntryStatusId === 1)
    );
  }

  return false;
};

// TimesPage - FOR LIST VIEW ONLY
// QueryResultsPage and ApproveTimeByUserPage - SHOULD BE EQUAL TO TIMES PAGES, EXCEPT TO COMPARE EACH ENTRY USER WITH ACTUAL USER
export const iconEditEnabled = (
  entry: EntryListView | TimeEntryApprovalTime,
  activeUserId: any,
  userId: any,
  isSuperTimeAdmin: boolean,
  isSupervisorOrPO: boolean,
  isDeliveryTimeAdmin: boolean,
  isTimeViewer: boolean,
  previousWorkableDay: Date | null,
  today: Date | null,
  lastWorkableDay: Date | null
) => {
  let isImportedFromChangeGear: boolean = entry.timeEntrySourceId === 3;
  let isImportedFromBambooNotHoliday: boolean =
    isImportedFromBambooNotHolidayChecker(entry);
  let isTimeEntryLocked: boolean = entry.timeEntryStatusId === 3;

  // FOR HIMSELF
  if (
    activeUserId === userId &&
    !isImportedFromChangeGear &&
    !isImportedFromBambooNotHoliday &&
    !isTimeEntryLocked &&
    !isClosed(entry.entityStatus) &&
    !isProjectClosed(entry)
  ) {
    return isIconEnabledForSelf(
      entry,
      { isSuperTimeAdmin, isSupervisorOrPO, isDeliveryTimeAdmin, isTimeViewer },
      previousWorkableDay,
      today,
      lastWorkableDay,
      entry.canEdit
    );
  } else if (
    activeUserId !== userId &&
    !isImportedFromChangeGear &&
    !isTimeEntryLocked &&
    !isImportedFromBambooNotHoliday &&
    !isClosed(entry.entityStatus) &&
    !isProjectClosed(entry)
  ) {
    // FOR OTHERS
    return isIconEnabledForOthers(
      entry,
      { isSuperTimeAdmin, isSupervisorOrPO, isDeliveryTimeAdmin, isTimeViewer },
      previousWorkableDay,
      today,
      entry.canEdit,
      canEditHolidayFromBamboo(entry, activeUserId, userId)
    );
  }

  return false;
};

// TimesPage - FOR LIST VIEW ONLY
// QueryResultsPage and ApproveTimeByUserPage - SHOULD BE EQUAL TO TIMES PAGES, EXCEPT TO COMPARE EACH ENTRY USER WITH ACTUAL USER
// EntryHoursBox - ON DELETE ON MULTIPLE TIME ENTRIES = type of first param: TimeEntrySheetView
export const iconDeleteEnabled = (
  entry: EntryListView | TimeEntrySheetView,
  activeUserId: any,
  userId: any,
  isSuperTimeAdmin: boolean,
  isSupervisorOrPO: boolean,
  isDeliveryTimeAdmin: boolean,
  isTimeViewer: boolean,
  previousWorkableDay: Date | null,
  today: Date | null,
  lastWorkableDay: Date | null
) => {
  let isImportedFromChangeGear: boolean = entry.timeEntrySourceId === 3;
  let isTimeEntryLocked: boolean = entry.timeEntryStatusId === 3;
  let isImportedFromBambooNotHoliday: boolean =
    isImportedFromBambooNotHolidayChecker(entry);

  // FOR HIMSELF
  if (
    activeUserId === userId &&
    !isImportedFromChangeGear &&
    !isImportedFromBambooNotHoliday &&
    !isTimeEntryLocked &&
    !isClosed(entry.entityStatus) &&
    !isProjectClosed(entry)
  ) {
    return isIconEnabledForSelf(
      entry,
      { isSuperTimeAdmin, isSupervisorOrPO, isDeliveryTimeAdmin, isTimeViewer },
      previousWorkableDay,
      today,
      lastWorkableDay,
      entry.canDelete
    );
  } else if (
    activeUserId !== userId &&
    !isImportedFromChangeGear &&
    (!isImportedFromBambooNotHoliday ||
      // ASUITE11-2736: can delete Paid Time Off(taskTypeId:32) or Unpaid Time Off(taskTypeId:42) from bamboo for others
      (isImportedFromBambooNotHoliday && isSuperTimeAdmin)) &&
    (!isTimeEntryLocked ||
      // ASUITE11-2736: can delete Paid Time Off(taskTypeId:32) or Unpaid Time Off(taskTypeId:42) from bamboo for others
      (isTimeEntryLocked &&
        isImportedFromBambooNotHoliday &&
        isSuperTimeAdmin)) &&
    !isClosed(entry.entityStatus) &&
    !isProjectClosed(entry)
  ) {
    // FOR OTHERS
    return isIconEnabledForOthers(
      entry,
      { isSuperTimeAdmin, isSupervisorOrPO, isDeliveryTimeAdmin, isTimeViewer },
      previousWorkableDay,
      today,
      entry.canDelete,
      isImportedFromBambooNotHoliday
    );
  }

  return false;
};

export const preparedFiltersWhoIsOut = (
  filters: any,
  projectsWhoIsOut: any,
  isSuperTimeAdmin: boolean,
  isSupervisorOrPO: boolean,
  isTimeViewer: boolean
) => {
  const now = new Date();
  let actualEndDate;
  switch (filters.timeFrame.value) {
    case 1:
      actualEndDate = new Date(
        now.getTime() + 30 * 24 * 60 * 60 * 1000
      ).toISOString();
      break;
    case 2:
      actualEndDate = new Date(
        now.getTime() + 90 * 24 * 60 * 60 * 1000
      ).toISOString();
      break;
    case 3:
      actualEndDate = new Date(
        now.getTime() + 180 * 24 * 60 * 60 * 1000
      ).toISOString();
      break;
    case 4:
      actualEndDate = new Date(new Date().getFullYear(), 11, 31).toISOString();
      break;
    default:
      break;
  }

  // cost Centers
  let costCenterInfo: any = [];
  if (filters.costCenter && filters.costCenter.length > 0) {
    filters.costCenter.forEach((costCenter) => {
      costCenterInfo = costCenterInfo.concat(costCenter.value);
    });
  }
  // projects
  let projectInfo: any = [];
  if (filters.project && filters.project.length > 0) {
    filters.project.forEach((project) => {
      projectInfo.push(project.value);
    });
  } else if (
    projectsWhoIsOut &&
    isSupervisorOrPO &&
    !isSuperTimeAdmin &&
    !isTimeViewer
  ) {
    projectsWhoIsOut.forEach((project) => {
      projectInfo.push(project.value);
    });
  }

  let response: FiltersWhoIsOutBody = {
    startDate: now.toISOString(),
    endDate: actualEndDate,
    costCentersIds: sendNullIfEmpty(costCenterInfo),
    projectsIds: sendNullIfEmpty(projectInfo),
  };
  return response;
};

export const addElementToArray = (arrayToAdd: any, arrayToLoop: any) => {
  arrayToLoop.forEach((elem) => {
    arrayToAdd.push({
      value: elem.id,
      label: elem.id + " - " + elem.name,
      level: elem.level,
      departmentId: elem.departmentId,
    });
    if (elem.children && elem.children.length > 0) {
      addElementToArray(arrayToAdd, elem.children);
    }
  });
};

export const getCostCenterChildrenIds = (
  structure: Array<any>,
  targetId: number,
  fieldForSearch: string
) => {
  const result: any = [];

  function findNodeAndCollectIds(node) {
    if (node[fieldForSearch] === targetId) {
      collectIds(node);
      return true; // Node found
    }

    if (node.children) {
      for (const child of node.children) {
        if (findNodeAndCollectIds(child)) return true;
      }
    }
    return false; // Node not found in this branch
  }

  function collectIds(node: any) {
    result.push(node[fieldForSearch]);
    if (node.children) {
      for (const child of node.children) {
        collectIds(child);
      }
    }
  }

  for (const item of structure) {
    if (findNodeAndCollectIds(item)) break;
  }

  return result;
};

export const getUserNameFromArray = (users: any[], userId: string) => {
  let response: any[] = users?.filter((user) => user.value === userId);

  if (response.length === 0) {
    return null;
  }
  return response[0].label;
};

export const getTooltipText = (entry: any) => {
  if (entry.categoryId === 2 && isClosed(entry.entityStatus)) {
    return "Closed task";
  } else if (entry.categoryId === 3 && isClosed(entry.entityStatus)) {
    return "Closed WO";
  } else {
    return "Edit";
  }
};
